<script setup lang="ts">
  import { useCurrentElement, useScroll, useThrottleFn } from '@vueuse/core'
  import { vIntersectionObserver } from '@vueuse/components'
  import { computed, toRefs, type Ref } from 'vue'
  import { useAutoRefresh } from '@/composables/useAutoRefresh'

  const emit = defineEmits<{
    (e: 'loadTop'): void
    (e: 'loadBottom'): void
  }>()

  const props = defineProps({
    list: {
      type: Array,
      required: true
    },
    size: {
      type: Number,
      default: 20 // Nombre d'éléments ajoutés à chaque fois.
    },
    offset: {
      type: Number,
      default: 1000 // Marge supplémentaire de défilement autorisée, en pixels.
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    noMore: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  })
  const { list, size, offset, isLoading, noMore, error } = toRefs(props)

  const items = computed(() => list.value.map(renderItem))

  function renderItem(data: any) {
    return {
      data
    }
  }

  const { resume, pause } = useAutoRefresh(() => {
    if (isLoading.value) return
    emit('loadTop')
  })

  const onColumnIntersect = useThrottleFn(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (!entry) return
      if (entry.isIntersecting) {
        resume()
      } else {
        pause()
      }
    },
    1000,
    false,
    true
  )

  const onLoadingIntersect = (entries: IntersectionObserverEntry[]) => {
    const entry = entries[0]
    if (!entry) return
    if (entry.isIntersecting) {
      emit('loadBottom')
    }
  }

  const el = useCurrentElement() as Ref<HTMLElement>
  const { y, directions } = useScroll(el, { behavior: 'smooth' })

  function scrollToTop() {
    y.value = 0
  }

  defineExpose({
    directions,
    y,
    scrollToTop
  })
</script>

<template>
  <nav
    class="vue-recyclist scrollbar-thumb-base-300 scrollbar-track-base-200 scrollbar-thin overflow-x-hidden overflow-y-scroll overscroll-y-none"
    v-intersection-observer="[onColumnIntersect, { threshold: [0.5] }]"
  >
    <div
      v-if="!isLoading && error"
      class="text-error m-4 text-center"
    >
      <slot name="error">
        <div>Error</div>
      </slot>
    </div>
    <div
      v-for="item in items"
      :key="`doc-${item.data}`"
      class="w-full"
    >
      <slot
        :data="item.data"
        name="item"
      />
    </div>
    <div
      v-for="(_, index) in size"
      v-show="isLoading"
      :key="`tombstone-${index}`"
      class="w-full"
    >
      <slot name="tombstone" />
    </div>
    <!-- Sentinelle masquée pendant le chargement : sa réapparition regénère
         un événement d'intersection, donc un retry d'un load-bottom ignoré -->
    <div
      v-show="!noMore && !error && !isLoading"
      v-intersection-observer="[
        onLoadingIntersect,
        { rootMargin: `${offset}px` }
      ]"
    >
      &nbsp;
    </div>
    <div
      v-if="!isLoading && noMore"
      class="m-4 text-center"
    >
      <slot name="nomore">
        <div>End of list</div>
      </slot>
    </div>
  </nav>
</template>

<style scoped>
  .vue-recyclist {
    -webkit-overflow-scrolling: touch;
    contain: strict;
  }
  .vue-recyclist-item {
    content-visibility: auto;
    contain-intrinsic-size: 290px;
  }
</style>
